<script lang="ts">
/** One row of whichever entity tab is showing — the tabs differ only in copy. */
interface EntityRow {
  uid: string;
  /** Backend entity type, as the link/unlink endpoints name it. */
  type: string;
  title: string;
  /** Caps tag before the title (clause category, obligation type). */
  category?: string | null;
  /** Mono reference after the title (clause number, country code). */
  ref?: string | null;
  body?: string | null;
  /**
   * Why the caller can't unlink it (a party they can't edit — the link is
   * part of the party too); null when they can.
   */
  unlinkBlock?: string | null;
}
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { type RouteLocationRaw, useRouter } from 'vue-router';
import {
  addContractualPartyToContractFile,
  removeContractualPartyFromContractFile,
  type ContractManagementObligationInDb,
  type ContractManagementClauseInDb,
} from '@/api';
import {
  PARTY_KIND_LABELS,
  type ContractualParty,
} from '@/services/contractManagement/contractualPartyRow';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';
import SelectEntityModal from '@/components/contractManagement/SelectEntityModal.vue';
import type { EntityType } from '@/types/contractManagement/entityType';
import { logger } from '@/utils/logger';
import { ACCESS_PERMISSION_WRITE, deniedReason } from '@/utils/accessPermission';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaTabBar from '@/components/design/composite/GaiaTabBar.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import { gaiaFonts, gaiaNeutral } from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';

// ==================== Props ====================
interface Props {
  contractFileUid: string;
  // Data passed from parent to avoid refetching
  obligations: ContractManagementObligationInDb[];
  clauses: ContractManagementClauseInDb[];
  contractualParties: ContractualParty[];
  loading: boolean;
}

const props = defineProps<Props>();

// ==================== Emits ====================
const emit = defineEmits<{
  refresh: [];
}>();

// ==================== Router & State ====================
const router = useRouter();
const message = useGaiaToast();

// Tab state — persisted per file in sessionStorage
const TAB_KEYS = new Set(['clauses', 'obligations', 'parties']);
// A tab stored before Locations was removed falls back to the first one.
const getStoredTab = (): string => {
  const stored = sessionStorage.getItem(`static-models-tab-${props.contractFileUid}`);
  return stored && TAB_KEYS.has(stored) ? stored : 'parties';
};
const activeTab = ref<string>(getStoredTab());
watch(activeTab, tab => {
  sessionStorage.setItem(`static-models-tab-${props.contractFileUid}`, tab);
});

// Track which entity is currently being unlinked (only one at a time)
const unlinkingUid = ref<string | null>(null);
/** The row whose ✕ was pressed — its strip replaces the row until answered. */
const confirmingUid = ref<string | null>(null);

// Modal state for adding entities
const showAddEntityModal = ref<boolean>(false);
const addEntityType = ref<EntityType | null>(null);

// ==================== Tabs ====================
/**
 * Each tab names its own copy — the empty state and the Add button read as the
 * thing being linked, not as a generic "item".
 */
const tabs = computed(() => [
  {
    key: 'parties',
    label: 'Parties',
    noun: 'party',
    count: props.contractualParties.length,
    icon: 'M5.5 3.2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM1.8 12.8c.4-2 1.9-3.1 3.7-3.1s3.3 1.1 3.7 3.1M9.5 6.5h5v6.3h-5z',
  },
  {
    key: 'clauses',
    label: 'Clauses',
    noun: 'clause',
    count: props.clauses.length,
    icon: 'M4 2.5h6l4 4v9H4z M10 2.5v4h4',
  },
  {
    key: 'obligations',
    label: 'Obligations',
    noun: 'obligation',
    count: props.obligations.length,
    icon: 'M3 4.5h10M3 9h10M3 13.5h6',
  },
]);

const currentTab = computed(() => tabs.value.find(t => t.key === activeTab.value) ?? tabs.value[0]);

/**
 * The glyph for a tab, by key. GaiaTabBar hands its `#icon` slot the tab it is
 * drawing, not this file's richer row, so the path is looked up rather than
 * read off the slot prop.
 */
function iconFor(key: string): string {
  return tabs.value.find(tab => tab.key === key)?.icon ?? '';
}

const addTypeMap: Record<string, EntityType> = {
  clauses: 'clause',
  obligations: 'obligation',
  parties: 'contractual-party',
};

const currentAddType = computed<EntityType>(() => addTypeMap[activeTab.value] ?? 'clause');

/**
 * The active tab's records, flattened to one row shape. Every tab draws the
 * same row — tag, title, reference, body — so the branching happens once here
 * rather than five times in the template.
 */
const rows = computed<EntityRow[]>(() => {
  switch (activeTab.value) {
    case 'obligations': {
      return props.obligations.map(obligation => ({
        uid: obligation.uid,
        type: 'obligation',
        title: obligation.name || 'Unnamed Obligation',
        category: obligation.type,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
        body: obligation.description || obligation.trigger_event,
      }));
    }
    case 'parties': {
      return props.contractualParties.map(party => ({
        uid: party.uid,
        type: 'contractual_party',
        title: party.name,
        unlinkBlock: deniedReason(party.permissions, ACCESS_PERMISSION_WRITE),
        body: [PARTY_KIND_LABELS[party.kind], party.city, party.country_name]
          .filter(Boolean)
          .join(' · '),
      }));
    }
    default: {
      return props.clauses.map(clause => ({
        uid: clause.uid,
        type: 'clause',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        title: clause.title || 'Untitled Clause',
        category: clause.category,
        ref: clause.clause_number ? `#${clause.clause_number}` : null,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
        body: clause.description || clause.text,
      }));
    }
  }
});

// ==================== Functions ====================
/**
 * Request parent to refresh static models data
 */
function refreshStaticModels(): void {
  emit('refresh');
}

// Handle adding new entity
function handleAddEntity(entityType: EntityType): void {
  addEntityType.value = entityType;
  showAddEntityModal.value = true;
}

// Handle entity assigned from modal
async function handleEntityAssigned(entityUid: string, pageNumber?: number): Promise<void> {
  if (!addEntityType.value) {
    message.error('Entity type is missing');
    return;
  }

  try {
    const pageNum = pageNumber !== undefined && pageNumber >= 0 ? pageNumber : null;

    switch (addEntityType.value) {
      case 'obligation':
        await ContractFilesService.addObligation(entityUid, props.contractFileUid, pageNum);
        message.success('Obligation assigned successfully');
        refreshStaticModels();
        break;
      case 'clause':
        await ContractFilesService.addClause(entityUid, props.contractFileUid, pageNum);
        message.success('Clause assigned successfully');
        refreshStaticModels();
        break;
      case 'contractual-party':
        await addContractualPartyToContractFile({
          query: {
            contractual_party_uid: entityUid,
            contract_file_uid: props.contractFileUid,
          },
          throwOnError: true,
        });
        message.success('Contractual party assigned successfully');
        refreshStaticModels();
        break;
      default:
        message.error('Unsupported entity type');
        return;
    }
  } catch (error) {
    logger.error('Error assigning entity:', error);
    const errorMsg =
      error instanceof Error ? error.message : 'Failed to assign entity. Please try again.';
    message.error(errorMsg);
  }
}

// Get assigned entity UIDs for filtering in modal
const assignedEntityUids = computed(() => {
  switch (addEntityType.value) {
    case 'obligation':
      return props.obligations.map(o => o.uid);
    case 'clause':
      return props.clauses.map(c => c.uid);
    case 'contractual-party':
      return props.contractualParties.map(p => p.uid);
    default:
      return [];
  }
});

// Entity detail route for a row
function entityRoute(entityType: string, entityUid: string): RouteLocationRaw {
  // Convert underscore format to hyphen format for routing
  const routeEntityType = entityType.replace(/_/g, '-');

  return {
    name: 'contract-management-detail',
    params: {
      entityType: routeEntityType,
      uid: entityUid,
    },
  };
}

// Handle unlink entity from contract file
async function handleUnlinkEntity(row: EntityRow): Promise<void> {
  confirmingUid.value = null;
  unlinkingUid.value = row.uid;

  try {
    switch (row.type) {
      case 'obligation':
        await ContractFilesService.removeObligation(row.uid, props.contractFileUid);
        break;
      case 'clause':
        await ContractFilesService.removeClause(row.uid, props.contractFileUid);
        break;
      case 'contractual_party':
        await removeContractualPartyFromContractFile({
          query: { contractual_party_uid: row.uid, contract_file_uid: props.contractFileUid },
          throwOnError: true,
        });
        break;
      default:
        message.error('Unsupported entity type for unlink operation');
        return;
    }
    message.success(`${row.title} unlinked successfully`);
    // Request parent to refresh after successful unlink
    refreshStaticModels();
  } catch (error) {
    logger.error('Error unlinking entity:', error);
    const errorMsg =
      error instanceof Error ? error.message : 'Failed to unlink entity. Please try again.';
    message.error(errorMsg);
  } finally {
    unlinkingUid.value = null;
  }
}

const tabColor = gaiaNeutral.textTertiary;
const cardBorder = gaiaNeutral.borderCard;
const surface = gaiaNeutral.surface;
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
const tagBorder = gaiaNeutral.border;
const mutedColor = gaiaNeutral.textMuted;
const shimmerBase = gaiaNeutral.surfaceSoft;
const shimmerHighlight = gaiaNeutral.surfaceTint;
const monoFont = gaiaFonts.mono;
</script>

<template>
  <!-- The Entities block from the Contract File Detail mock: one card whose
       tab bar carries the counts and the Add action. -->
  <n-flex vertical :size="12" class="static-models-section">
    <div class="entities-card" data-tour="cm.file-entities">
      <gaia-tab-bar
        variant="card"
        :items="tabs"
        :active="activeTab"
        @select="(key: string) => (activeTab = key)"
      >
        <template #icon="{ item }">
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path :d="iconFor(item.key)" />
          </svg>
        </template>
        <template #actions>
          <gaia-add-button :subject="currentTab.noun" @click="handleAddEntity(currentAddType)" />
        </template>
      </gaia-tab-bar>

      <div class="tab-content">
        <template v-if="props.loading">
          <div v-for="index in 3" :key="index" class="shimmer" />
        </template>

        <gaia-empty-state v-else-if="rows.length === 0" :title="`No ${currentTab.noun} linked yet`">
          <template #icon>
            <svg
              width="16"
              height="16"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M4 2.5h6l4 4v9H4z" />
              <path d="M10 2.5v4h4" />
            </svg>
          </template>
          Linking one records that this {{ currentTab.noun }} appears in the document, so it is
          reachable from either side.
          <template #actions>
            <gaia-add-button
              :subject="currentTab.noun"
              :icon="false"
              @click="handleAddEntity(currentAddType)"
            />
          </template>
        </gaia-empty-state>

        <template v-for="row in rows" v-else :key="row.uid">
          <!-- Unlink is destructive, so it answers in the row's own place
               rather than in a popconfirm floating over the list. -->
          <gaia-table-row-state
            v-if="confirmingUid === row.uid"
            variant="confirm"
            confirm-label="Unlink"
            :title="`Unlink “${row.title}”?`"
            @cancel="confirmingUid = null"
            @confirm="handleUnlinkEntity(row)"
          >
            The record itself stays — only its link to this file is removed.
          </gaia-table-row-state>

          <gaia-table-row-state v-else-if="unlinkingUid === row.uid" variant="busy">
            Unlinking “{{ row.title }}”…
          </gaia-table-row-state>

          <div v-else class="entity-row" @click="router.push(entityRoute(row.type, row.uid))">
            <n-flex vertical :size="5" :style="{ flex: 1, minWidth: 0 }">
              <n-flex align="center" :size="9">
                <span v-if="row.category" class="category">{{ row.category }}</span>
                <gaia-text size="row" weight="semibold" tone="ink">
                  <router-link
                    :to="entityRoute(row.type, row.uid)"
                    style="color: inherit; text-decoration: none"
                    @click.stop
                  >
                    {{ row.title }}
                  </router-link>
                </gaia-text>
                <gaia-text v-if="row.ref" size="micro" class="ref">{{ row.ref }}</gaia-text>
              </n-flex>
              <gaia-text v-if="row.body" size="control" tone="tertiary" class="body">
                {{ row.body }}
              </gaia-text>
            </n-flex>

            <gaia-delete-button
              intent="remove"
              class="unlink"
              subject="from this file"
              :disabled="Boolean(row.unlinkBlock)"
              :title="row.unlinkBlock ?? undefined"
              @click.stop="confirmingUid = row.uid"
            />
          </div>
        </template>
      </div>
    </div>

    <select-entity-modal
      v-if="addEntityType"
      v-model:show="showAddEntityModal"
      :entity-type="addEntityType"
      :contract-file-uid="props.contractFileUid"
      :assigned-entity-uids="assignedEntityUids"
      @assigned="handleEntityAssigned"
    />
  </n-flex>
</template>

<style scoped>
.static-models-section {
  min-width: 0;
}

/* Same card chrome as GaiaDataTable, but the header strip here is a tab bar
   rather than a column header, so the card is drawn locally. */
.entities-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid v-bind(cardBorder);
  border-radius: 10px;
  background: v-bind(surface);
}

.tab-content {
  display: flex;
  flex-direction: column;
}

.entity-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 13px 16px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.entity-row:last-child {
  border-bottom: none;
}

.entity-row:hover {
  background: v-bind(rowHover);
}

/* A caps tag on a row, sized to the line rather than to GaiaTag's chip box. */
.category {
  display: inline-flex;
  align-items: center;
  flex: none;
  height: 19px;
  padding: 0 7px;
  border: 1px solid v-bind(tagBorder);
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: v-bind(tabColor);
}

/* References are compared down the list, so they take the mono face. */
.ref {
  flex: none;
  color: v-bind(mutedColor);
  font-family: v-bind(monoFont);
}

.body {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.55;
}

/* Unlink stays in the DOM so it is reachable by keyboard; focus reveals it the
   way hover does. */
/* Revealed only while the row is pointed at — a list of entities shouldn't
   read as a list of unlink buttons. Colour and hover wash are the button's. */
.unlink {
  flex: none;
  opacity: 0;
}

.entity-row:hover .unlink,
.unlink:focus-visible {
  opacity: 1;
}

.shimmer {
  height: 52px;
  margin: 5px 16px;
  border-radius: 9px;
  background: linear-gradient(
    90deg,
    v-bind(shimmerHighlight) 0%,
    v-bind(shimmerBase) 45%,
    v-bind(shimmerHighlight) 90%
  );
  background-size: 480px 100%;
  animation: entity-shimmer 1.5s linear infinite;
}

.shimmer:first-child {
  margin-top: 14px;
}

.shimmer:last-child {
  margin-bottom: 14px;
}

@keyframes entity-shimmer {
  0% {
    background-position: -280px 0;
  }

  100% {
    background-position: 480px 0;
  }
}
</style>
